import React, { useState } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';

// Import configs and util modules
import { FormattedMessage } from '../../../../util/reactIntl';
import { LISTING_STATE_DRAFT } from '../../../../util/types';

// Import shared components
import { H3, ListingLink } from '../../../../components';

// Import modules from this directory
import EditListingServicesForm from './EditListingServicesForm';
import AddedServices from './AddedServices';
import { generateNumberArray, generateRandomID } from './utils';

import css from './EditListingServicesPanel.module.css';

const getInitialValues = props => {
  const { publicData = {} } = props?.listing?.attributes || {};
  const { services = [] } = publicData;
  if (!!services.length) return services.map(service => ({ ...service, id: generateRandomID() }));
  return [];
};

const EMPTY_SERVICE = {
  serviceName: null,
  servicePrice: null,
  serviceDuration: null,
  serviceDescription: null,
};

const EditListingServicesPanel = props => {
  const {
    className,
    rootClassName,
    listing,
    disabled,
    ready,
    onSubmit,
    submitButtonText,
    panelUpdated,
    updateInProgress,
    errors,
    currency,
  } = props;

  const classes = classNames(rootClassName || css.root, className);
  const isPublished = listing?.id && listing?.attributes.state !== LISTING_STATE_DRAFT;
  const avalilableDurations = generateNumberArray(1, 10);

  const initialValues = getInitialValues(props);
  const [addedServices, setAddedServices] = useState(initialValues);
  const [activeService, setActiveService] = useState(EMPTY_SERVICE);
  const [formHasChanged, setFormHasChanged] = useState(false);

  const handleAddNewService = newService => {
    let allServices;
    const serviceIndex = addedServices.findIndex(service => service.id === newService.id);
    if (serviceIndex > -1) {
      allServices = [
        ...addedServices.slice(0, serviceIndex),
        { ...newService },
        ...addedServices.slice(serviceIndex + 1),
      ];
    } else {
      allServices = [...addedServices, { ...newService, id: generateRandomID() }];
    }
    setAddedServices(allServices);
    setActiveService(EMPTY_SERVICE);
    setFormHasChanged(true);
  };

  const handleDeleteService = serviceId => {
    const allServices = addedServices.filter(({ id }) => id !== serviceId);
    setAddedServices(allServices);
    setFormHasChanged(true);
  };

  const onEditService = serviceId => {
    const edittingService = addedServices.find(({ id }) => id === serviceId);
    const newActiveService = edittingService ?? addedServices[0];
    setActiveService(newActiveService);
    setFormHasChanged(true);
  };

  const formattedServices = addedServices.map(
    ({ serviceName, servicePrice, serviceDuration, serviceDescription }) => ({
      serviceName,
      servicePrice,
      serviceDuration,
      serviceDescription,
    })
  );

  return (
    <div className={classes}>
      <H3 as="h1">{isPublished ? <div>Services</div> : <div>Services</div>}</H3>
      {!!addedServices.length && (
        <AddedServices
          services={addedServices}
          onEditService={onEditService}
          handleDeleteService={handleDeleteService}
        />
      )}
      <EditListingServicesForm
        className={css.form}
        initialValues={activeService}
        avalilableDurations={avalilableDurations}
        onAddNewService={handleAddNewService}
        formHasChanged={formHasChanged}
        onSubmit={values => {
          const { serviceName, servicePrice, serviceDuration, serviceDescription } = values;

          const newService = { serviceName, servicePrice, serviceDuration, serviceDescription };

          const allFieldsFilled = Object.values(newService).every(value => value != null);

          const updateValues = {
            publicData: {
              services: allFieldsFilled ? [...formattedServices, newService] : formattedServices,
            },
          };

          onSubmit(updateValues);
          setFormHasChanged(false);
        }}
        saveActionMsg={submitButtonText}
        hasServices={!!addedServices.length}
        currency={currency}
        disabled={disabled}
        ready={ready}
        updated={panelUpdated}
        updateInProgress={updateInProgress}
        fetchErrors={errors}
        autoFocus
      />
    </div>
  );
};

const { func, object, string, bool } = PropTypes;

EditListingServicesPanel.defaultProps = {
  className: null,
  rootClassName: null,
  listing: null,
};

EditListingServicesPanel.propTypes = {
  className: string,
  rootClassName: string,

  // We cannot use propTypes.listing since the listing might be a draft.
  listing: object,

  disabled: bool.isRequired,
  ready: bool.isRequired,
  onSubmit: func.isRequired,
  submitButtonText: string.isRequired,
  panelUpdated: bool.isRequired,
  updateInProgress: bool.isRequired,
  errors: object.isRequired,
  currency: string.isRequired,
};

export default EditListingServicesPanel;
